"use client"
import { useChains } from "@opensea/app-kit"
import type { ChainIdentifier, FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { Timeline, useToasts } from "@opensea/ui-kit"
import type { Awaitable } from "@opensea/utils"
import { useEffect } from "react"
import type { UseQueryExecute } from "urql"
import { useTranslations } from "use-intl"
import { useAccount } from "../../polychain/hooks"
import type { SchedulableResponse } from "../../polychain/hooks/useScheduler"
import { useScheduler } from "../../polychain/hooks/useScheduler"
import type { TransactionIdentifier } from "../../stores/currentTransactionStore"
import { FlowActions } from "../../stores/flowStore"
import { ActionTimelineFragment } from "./ActionTimeline.fragment"
import { ActionTimelineError } from "./ActionTimelineError"
import { ActionTimelineItem } from "./ActionTimelineItem"
import { ActionTimelineSkeleton } from "./ActionTimelineSkeleton"

export type ActionTimelineStatus = Pick<
  ReturnType<typeof useScheduler>,
  "isPending" | "isLoading" | "isDone"
>

type Props = {
  actions: FragmentOf<typeof ActionTimelineFragment>[]
  onReload?: UseQueryExecute
  onAllActionsCompleted?: (
    response: SchedulableResponse,
    receiptTransactions: TransactionIdentifier[],
  ) => Awaitable<void>
  onActionIndexChange?: (index: number) => void
  onActionCompleted?: (
    response: SchedulableResponse,
    index: number,
  ) => Awaitable<void>
  onBack?: () => void
  errorMessage?: string
  insufficientFundsOnChain?: ChainIdentifier
  onStatusChange?: (status: ActionTimelineStatus) => void
}

const ActionTimelineBase = ({
  actions: actionKeys,
  onAllActionsCompleted,
  onReload,
  onActionIndexChange,
  onActionCompleted,
  onBack,
  onStatusChange,
}: Props) => {
  const actions = actionKeys.map(a => readFragment(ActionTimelineFragment, a))

  const { current, retry, error, isLoading, start, isPending, isDone } =
    useScheduler({
      actions,
      onReload,
      onAllActionsCompleted,
      onActionIndexChange,
      onActionCompleted,
    })

  useEffect(
    function updateStatusOnChange() {
      onStatusChange?.({ isPending, isLoading, isDone })
    },
    [isPending, isLoading, isDone, onStatusChange],
  )

  return (
    <Timeline data-testid="timeline">
      {actions.map((action, i) => (
        <ActionTimelineItem
          action={action}
          active={current === i}
          error={error}
          isDone={current > i}
          isLoading={isLoading}
          isPending={isPending}
          key={i}
          onBack={onBack}
          retry={retry}
          start={start}
        />
      ))}
    </Timeline>
  )
}

export const ActionTimeline = (props: Props) => {
  const { actions, onBack, onReload, insufficientFundsOnChain, errorMessage } =
    props
  const t = useTranslations("wallet.ActionTimeline")

  const { ready, isConnecting, address } = useAccount()
  const { getName } = useChains()

  const { showErrorMessage } = useToasts()

  useEffect(
    function endFlowWhenWalletIsDisconnected() {
      if (ready && !address && !isConnecting) {
        showErrorMessage(t("walletDisconnected"))
        FlowActions.endFlow()
      }
    },
    [address, isConnecting, ready, showErrorMessage, t],
  )

  // On initial page load, the previously connected account is optimistically in
  // the account store and flows can be started while the wallet is still connecting.
  // Wait for the wallet to be connected before starting actions.
  if (!ready || isConnecting) {
    return <ActionTimelineSkeleton count={actions.length || 1} />
  }

  if (!address) {
    // Wallet is disconnected. Flow will be ended by useEffect above.
    return null
  }

  if (insufficientFundsOnChain) {
    return (
      <ActionTimelineError
        message={t("insufficientFundsOnChain", {
          chain: getName(insufficientFundsOnChain),
        })}
        showSide={false}
      />
    )
  }

  if (actions.length === 0) {
    return (
      <ActionTimelineError
        message={errorMessage ?? t("loadingActionsError")}
        onBack={onBack}
        onRetry={() => onReload?.()}
      />
    )
  }

  return <ActionTimelineBase {...props} />
}
